import React from "react";
import { ShoppingCart } from "lucide-react";
import { Card, CardHeader, CardBody, CardTitle, CardText, Badge } from "reactstrap";
import { extractTierFromName, getProductTierBadgeColor } from "../../../../../utility/inventoryUtils";
import './index.css'
import { ProductSummary } from "../../../../../hooks/useFirebaseProductSummaries";

interface ByCartTabProps {
  productSummaries: ProductSummary[];
}

export const ByCartTab: React.FC<ByCartTabProps> = ({ productSummaries }) => {

  if (!productSummaries || productSummaries.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-warning">
          <h5>No product data available</h5>
          <p className="mb-0">No product summaries data found. Please check if data exists in Firebase.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-by-cart-tab-content">
      {productSummaries.map((product) => {
        const tier = extractTierFromName(product.displayName);
        
        // Calculate totals for this product
        const totalAllocated = Object.values(product.channels || {}).reduce((sum, channel) => sum + (channel.allocated || 0), 0);
        const totalOrders = Object.values(product.channels || {}).reduce((sum, channel) => sum + (channel.reserved || 0), 0);

        return (
          <Card key={product.id} className="inventory-by-cart-card mb-4">
            <CardHeader className="inventory-by-cart-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <CardTitle className="inventory-by-cart-product-name h6 mb-1">
                    {product.displayName}
                  </CardTitle>
                  <CardText className="inventory-by-cart-product-sku text-muted small mb-0">
                   {product.productId || "No Product ID"} | Allocated: {totalAllocated} | Orders: {totalOrders}
                  </CardText>
                </div>
                <Badge
                  color={getProductTierBadgeColor(tier)}
                  className="inventory-by-cart-tier-badge"
                >
                  {tier}
                </Badge>
              </div>
            </CardHeader>
            <CardBody className="inventory-by-cart-card-body">
              <div className="inventory-cart-list">
                {Object.entries(product.carts || {}).map(([cartId, cart]) => {
                  // Calculate cart totals from cart.channels (not product.channels)
                  const cartAllocated = Object.values(cart.channels || {}).reduce((sum, channel) => sum + (channel.allocated || 0), 0);
                  
                  return (
                    <div key={cartId} className="inventory-cart-item p-2 mb-3 rounded">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="inventory-cart-info d-flex align-items-center">
                          <div className="inventory-cart-icon-container rounded-circle p-2 me-3">
                            <ShoppingCart className="text-white" size={20} />
                          </div>
                          <div>
                            <span className="inventory-cart-id fw-bold text-uppercase d-block">
                              {cart.cartName || cartId.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="text-end">
                            <div className="text-muted small">Allocated</div>
                          <span className="inventory-cart-total h4 fw-bold">
                            {cartAllocated}
                          </span>
                        
                        </div>
                      </div>
                      
                      {/* Channel allocated counts with background colors - using cart.channels */}
                      <div className="inventory-cart-channels">
                        <div className="inventory-cart-channel-line">
                          {Object.entries(cart.channels || {}).map(([channelName, channel], index) => (
                            <React.Fragment key={channelName}>
                              <div className="inventory-cart-channel-pair">
                                <span className="inventory-cart-channel-label">
                                  {channelName === "webDelivery" ? "WebDel" : 
                                   channelName === "whatsapp" ? "WA" :
                                   channelName === "walkin" ? "Walk" :
                                   channelName}
                                </span>
                                <span className="inventory-cart-channel-value">
                                  {/* Changed from channel.reserved to channel.allocated */}
                                  {channel.allocated || 0}
                                </span>
                              </div>
                              {index < Object.entries(cart.channels || {}).length - 1 && (
                                <span className="inventory-cart-channel-separator"> </span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
};